import React, { useEffect, useState } from 'react';
import styles from './AddPartModal.module.scss';
import { Button, Modal, FormControl, MenuItem, FormHelperText } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GetSystemResponse,
  GetGroupedPartCategoriesResponse,
  GroupedPartCategory,
  Option,
  PartCategoryToAdd,
  PartCategory,
  Group,
  Part,
  CreateManifoldRequest,
  CreateGroupRequest,
  OverlapGroupLinkResponse,
} from '../../models/Api';
import SystemService from '../../services/SystemService';
import {
  getUniqueName,
  createGroup,
  createPart,
  getManifoldGroups,
  sortByOrder,
  createValveRequest,
  updateOverlapGroupLinkWithLinkedPart,
  isPDNDivision,
  isManifoldOptimized,
  confirmManifoldRevert,
} from '../../utils/Utils';
import { AddPartMode } from '../../models/AddPartMode';
import ManifoldsDropdownList from '../ManifoldDropdownList/ManifoldsDropdownList';
import { toast } from 'react-toastify';
import Constants from '../../utils/Constants';

type ModalPropsType = {
  system: GetSystemResponse | undefined;
  modalState: boolean;
  mode: AddPartMode;
  previousPart?: Part;
  currentGroup?: Group;
  changeModalState: (show: boolean) => void;
};

type ValveWithMountingOption = {
  partCategoryToAdd: PartCategoryToAdd;
  mountingOption: ValveMountingOption;
  validate?: () => boolean;
};

enum ValveMountingOption {
  Individual = 'Individual',
  NewManifold = 'NewManifold',
  ExistingManifold = 'ExistingManifold',
}

type PartAssemblyOption = {
  categoryId: number;
  renderNumberOfManifoldStationsControl: JSX.Element;
  validate?: () => boolean;
};

type SelectedPart = {
  partCategoryToAdd?: PartCategoryToAdd;
  partListIndex: number;
  valveMountingOption?: ValveMountingOption;
  partAssemblyOption?: PartAssemblyOption;
  validate?: () => boolean;
};

export const AddPartModal = (props: ModalPropsType) => {
  const { t } = useTranslation();
  const [groupedPartCategoriesAndPartAssemblies, setGroupedPartCategoriesAndPartAssemblies] = useState<GetGroupedPartCategoriesResponse>(
    {} as GetGroupedPartCategoriesResponse,
  );
  const [valveCategory, setValveCategory] = useState<PartCategoryToAdd | undefined>(undefined);
  const [activePart, setActivePart] = useState<SelectedPart | undefined>({ partListIndex: 0 });
  const [activeGroup, setActiveGroup] = useState(0);
  const [numberOfManifoldStations, setNumberOfManifoldStations] = useState(0);
  const [manifolds, setManifolds] = useState<Group[]>([]);
  const [selectedManifoldGroupId, setSelectedManifoldGroupId] = useState<number>(-1);
  const [confirmedRevertManifold, setConfirmedRevertManifold] = useState(false);
  const [modalTitleKey, setModalTitleKey] = useState('');
  const [valveOptions, setValveOptions] = useState<ValveWithMountingOption[]>([]);
  const [validateNumberStations, setValidateNumberStations] = useState(false);
  const [validateManifolds, setValidateManifolds] = useState(false);
  const [showOptimizedManifoldAlert, setShowOptimizedManifoldAlert] = useState(false);
  const pneumaticDirectionalControlValvesCategory = props.system?.partCategories?.find(
    (partCategory: PartCategory) => partCategory.name === 'PneumaticDirectionalControlValves',
  );

  const pneumaticLineGroupDefinition = props.system?.groupDefinitions?.find((definition) => definition.uniqueName === 'PneumaticLine');

  const isValveCategory = (partCategoryId: number) => {
    const category = props.system?.partCategories?.find((partCategory) => partCategory.id === partCategoryId);
    return category && category.name === 'PneumaticDirectionalControlValves';
  };

  const isNumberOfStationsValid = () => {
    return numberOfManifoldStations > 0;
  };

  const validateSelectedNumberOfStations = () => {
    setValidateNumberStations(true);
    return isNumberOfStationsValid();
  };

  const validateSelectedManifoldGroupId = () => {
    setValidateManifolds(true);
    return selectedManifoldGroupId > -1;
  };

  const setSelectedManifold = (selectedManifoldId: string) => {
    if (selectedManifoldId) setSelectedManifoldGroupId(Number.parseInt(selectedManifoldId));
  };
  const getPartCategoriesWithoutValve = () => {
    let partCategories =
      groupedPartCategoriesAndPartAssemblies &&
      groupedPartCategoriesAndPartAssemblies?.groupedPartCategories &&
      groupedPartCategoriesAndPartAssemblies?.groupedPartCategories[activeGroup].partCategories;
    if (valveCategory) {
      partCategories = partCategories?.filter((part) => part.partCategoryId !== valveCategory.partCategoryId) as PartCategoryToAdd[];
    }
    return (partCategories || []) as PartCategoryToAdd[];
  };

  const getValveWithMountingOptions = () => {
    const valvePartCategory = valveCategory as PartCategoryToAdd;
    const valveWithOptions: ValveWithMountingOption[] = [];
    valveWithOptions.push({
      partCategoryToAdd: valvePartCategory,
      mountingOption: ValveMountingOption.Individual,
    });

    // Currently, we have an artificial limit of just one manifold per system, but that will change in the future
    if (manifolds.length > 0) {
      valveWithOptions.push({
        partCategoryToAdd: valvePartCategory,
        mountingOption: ValveMountingOption.ExistingManifold,
        validate: validateSelectedManifoldGroupId,
      });
    } else {
      valveWithOptions.push({
        partCategoryToAdd: valvePartCategory,
        mountingOption: ValveMountingOption.NewManifold,
        validate: validateSelectedNumberOfStations,
      });
    }

    return valveWithOptions;
  };

  const handleStationsChange = (event: SelectChangeEvent) => {
    setNumberOfManifoldStations(Number.parseInt(event.target.value));
  };

  const handleModalClose = () => {
    props.changeModalState(false);
  };

  useEffect(() => {
    setValveOptions(valveCategory ? getValveWithMountingOptions() : []);
  }, [valveCategory, manifolds, selectedManifoldGroupId, numberOfManifoldStations]);

  async function getPartCategoriesData() {
    let partCategoriesList: GetGroupedPartCategoriesResponse;
    let valvePartCategoryToAdd: PartCategoryToAdd | undefined;

    switch (props.mode) {
      case AddPartMode.FirstPartForGroup:
        partCategoriesList = await SystemService.getPartCategoriesList(props.system?.templateTypeId as number);
        setModalTitleKey('SelectFirstPartToAdd');
        break;
      case AddPartMode.InitialGroupAndPart:
        partCategoriesList = await SystemService.getPartCategoriesList(props.system?.templateTypeId as number);
        setModalTitleKey('SelectGroupAndInitialPartToAdd');
        break;
      case AddPartMode.SubsequentPartForGroup:
        partCategoriesList = await SystemService.getPartCategoriesList(
          props.system?.templateTypeId as number,
          props.previousPart?.categoryId,
          props.currentGroup?.definitionId,
          props.system?.partCategories?.find((category) => category.id === props.previousPart?.categoryId)?.name as string,
          undefined,
          props.system?.groupDefinitions?.find((definition) => definition.id === props.currentGroup?.definitionId)?.uniqueName as string,
        );
        setModalTitleKey('SelectPartToAdd');
        break;
      default:
        partCategoriesList = {};
        break;
    }

    if (props.mode === AddPartMode.SubsequentPartForGroup && props.system?.templateDashboardType === 'Manifold') {
      const groupedPartCategories = partCategoriesList.groupedPartCategories as GroupedPartCategory[];
      for (const groupedCategory of groupedPartCategories) {
        valvePartCategoryToAdd = groupedCategory.partCategories?.find((partCategory) => isValveCategory(partCategory.partCategoryId as number));
        if (valvePartCategoryToAdd) {
          setValveCategory(valvePartCategoryToAdd);
          break;
        }
      }
      setManifolds(getManifoldGroups(props.system) as Group[]);
    }

    setSelectedManifoldGroupId(-1);
    setNumberOfManifoldStations(0);

    setGroupedPartCategoriesAndPartAssemblies(partCategoriesList);
    setValveCategory(valvePartCategoryToAdd);
    setValidateNumberStations(false);
    setValidateManifolds(false);
  }

  const autoExecAction = () => {
    const action = sessionStorage.getItem(Constants.sessionStorageKeys.addPartDialogSelection);
    if (!action) {
      return;
    }
    const groupIndex = action === Constants.autoExecActions.selectManifoldAddPartDialog ? -1 : 0;
    setActiveGroup(groupIndex);
    sessionStorage.removeItem(Constants.sessionStorageKeys.addPartDialogSelection);
  };

  useEffect(() => {
    if (props.modalState) {
      getPartCategoriesData();
      autoExecAction();
    }
  }, [props.modalState]);

  const getInitialGroupCount = () => {
    return props.system?.parts?.filter((part) => part.column === 1).length as number;
  };

  async function createSelectedPart(groupId: number | undefined, selectedPart: PartCategoryToAdd | null | undefined) {
    const partsInGroup = props.system?.parts?.filter((part) => part.groupInstanceId === groupId);
    const lastPartInGroup = partsInGroup ? sortByOrder(partsInGroup)[partsInGroup.length - 1] : undefined;

    if (groupedPartCategoriesAndPartAssemblies.groupedPartCategories) {
      const selectedPartCategory = props.system?.partCategories?.find(
        (partCategory: PartCategory) => partCategory.id === (selectedPart && selectedPart.partCategoryId),
      );

      const baseName = selectedPartCategory?.options?.find((option) => option.addOnDashboard)?.displayShortName;

      const newPart = await createPart(
        groupId as number,
        selectedPart?.isLastPartInGroup as boolean,
        selectedPartCategory?.id as number,
        getUniqueName(props.system as GetSystemResponse, baseName as string, false, false),
        getInitialGroupCount() + 1,
        lastPartInGroup ? (lastPartInGroup.column as number) + 1 : 1,
        lastPartInGroup ? (lastPartInGroup.order as number) + 1 : 1,
      );

      const overlapLink = props.system?.overlapGroupLinks?.find((link) => link.overlappingGroupId === groupId) as OverlapGroupLinkResponse;
      if (overlapLink) {
        await updateOverlapGroupLinkWithLinkedPart(newPart, overlapLink);
      }
    }
  }

  function createFirstPart() {
    const selectedPart = activePart?.partCategoryToAdd;
    createSelectedPart(props.currentGroup?.id, selectedPart);
  }

  async function createGroupAndPart() {
    if (groupedPartCategoriesAndPartAssemblies?.groupedPartCategories) {
      const selectedGroup = groupedPartCategoriesAndPartAssemblies?.groupedPartCategories[activeGroup];

      const selectedPart = activePart?.partCategoryToAdd;

      const selectedGroupDefinition = props.system?.groupDefinitions?.find((groupDefinition) => groupDefinition.id === selectedGroup.group?.groupDefinitionId);

      const createGroupResponse = await createGroup(
        props.system?.id as string,
        selectedGroupDefinition?.id as number,
        getUniqueName(props.system as GetSystemResponse, selectedGroupDefinition?.displayName as string, true, false),
        selectedPart?.isLastPartInGroup,
        null,
      );

      if (createGroupResponse.status === 200) createSelectedPart(createGroupResponse.data.id, selectedPart);
    }
  }

  async function createManifold(hasExistingLinkedPart: boolean) {
    const selectedPartCategory = props.system?.partCategories?.find((partCategory: PartCategory) => partCategory.id === valveCategory?.partCategoryId);
    const partCategory = hasExistingLinkedPart ? selectedPartCategory : pneumaticDirectionalControlValvesCategory;
    const currentGroupDefinition = props.system?.groupDefinitions?.find((definition) => definition.id === props.currentGroup?.definitionId);
    const groupDefinition = hasExistingLinkedPart ? currentGroupDefinition : pneumaticLineGroupDefinition;
    const manifoldGroupDefinition = props.system?.groupDefinitions?.find((definition) => definition.uniqueName === 'PneumaticSupplyManifolds');
    const segmentGroupDefinition = props.system?.groupDefinitions?.find((definition) => definition.uniqueName === 'PneumaticManifoldSegment');

    const manifoldGroupRequest: CreateGroupRequest = {
      systemId: props.system?.id as string,
      definitionId: manifoldGroupDefinition?.id as number,
      name: getUniqueName(props.system as GetSystemResponse, manifoldGroupDefinition?.displayName as string, true, false),
      isFinished: true,
      parentGroupId: null,
      isLastInLine: false,
    };

    const segmentGroupRequest = {
      systemId: props.system?.id as string,
      definitionId: segmentGroupDefinition?.id as number,
      name: t('SegmentPlaceholder'),
      isFinished: true,
      parentGroupId: null,
      isLastInLine: false,
    };

    const createManifoldRequest: CreateManifoldRequest = {
      manifoldGroup: manifoldGroupRequest,
      segmentGroup: segmentGroupRequest,
      overlappingGroups: [],
      valves: [],
      existingOverlappingGroupId: hasExistingLinkedPart ? props.currentGroup?.id : null,
      existingLinkedPartId: hasExistingLinkedPart ? props.previousPart?.id : null,
    };

    const applicationGroupNames: { name: string }[] = [];

    const createApplicationGroupRequest = () => {
      const systemCopy = { ...props.system } as GetSystemResponse;
      if (applicationGroupNames.length > 0) {
        systemCopy.groups = [...(systemCopy.groups as Group[]), ...applicationGroupNames];
      }
      const uniqueName = getUniqueName(systemCopy, groupDefinition?.displayName as string, true, false);
      applicationGroupNames.push({ name: uniqueName });
      return {
        systemId: props.system?.id as string,
        definitionId: groupDefinition?.id as number,
        name: uniqueName,
        isFinished: false,
        isLastInLine: false,
        parentGroupId: null,
      };
    };

    //adding application groups
    for (let i = hasExistingLinkedPart ? 1 : 0; i < numberOfManifoldStations; i++) {
      createManifoldRequest.overlappingGroups?.push(createApplicationGroupRequest());
    }

    const manifoldPartCategory = props?.system?.partCategories?.find((category) => category.name === 'PneumaticManifoldMasterPart');
    const baseName = manifoldPartCategory?.options?.find((option) => option.addOnDashboard)?.displayShortName;
    createManifoldRequest.manifold = {
      groupInstanceId: 0,
      groupIsFinished: true,
      categoryId: manifoldPartCategory?.id,
      name: getUniqueName(props.system as GetSystemResponse, baseName as string, false, false),
      row: 1, // This should be OK for now, while the system can only have at most one manifold
      column: 2,
      order: 1,
    };

    //adding valves
    const valveNames: { name: string }[] = [];

    const partBaseName = partCategory?.options?.find((option) => option.addOnDashboard)?.displayShortName;

    for (let i = 0; i < numberOfManifoldStations; i++) {
      createManifoldRequest.valves?.push(
        createValveRequest(
          i,
          2,
          { ...props.system },
          partBaseName as string,
          hasExistingLinkedPart ? (valveCategory?.partCategoryId as number) : (pneumaticDirectionalControlValvesCategory?.id as number),
          valveNames,
        ),
      );
    }

    await SystemService.createManifold(createManifoldRequest as CreateManifoldRequest, hasExistingLinkedPart);
    return;
  }

  async function addToExistingManifold() {
    if (isManifoldOptimized(selectedManifoldGroupId)) {
      setShowOptimizedManifoldAlert(true);
      return;
    }
    const manifoldToBeLinked = props.system?.groups?.find((manifoldGroup) => manifoldGroup.id === selectedManifoldGroupId);
    const selectedPartCategory = props.system?.partCategories?.find((partCategory: PartCategory) => partCategory.id === valveCategory?.partCategoryId);

    const partBaseName = selectedPartCategory?.options?.find((option) => option.addOnDashboard)?.displayShortName;
    const valveName = getUniqueName(props?.system as GetSystemResponse, partBaseName as string, false, false);
    const linkedPartId = props?.previousPart?.id as string;

    SystemService.addValveToExistingManifold(manifoldToBeLinked?.id as number, {
      linkedPartId: linkedPartId,
      manifoldGroupId: manifoldToBeLinked?.id as number,
      name: valveName,
    });
  }
  useEffect(() => {
    if (confirmedRevertManifold) addToExistingManifold();
  }, [confirmedRevertManifold]);

  function createPartAssembly() {
    const systemHasManifold = props.system?.virtualGroups?.length;
    if (isPDNDivision(props.system?.divisionId) && systemHasManifold) {
      toast.error(t('YourSystemCanHaveOnlyOneManifold') as string);
      return;
    }
    createManifold(false);
  }

  function findValidOption(options: Option[]) {
    return options?.find((option) => option.addOnDashboard);
  }

  function renderNumberOfManifoldStationsControl() {
    return (
      <FormControl className={styles['modal-input-field']} size="small">
        <Select
          color="secondary"
          displayEmpty
          value={numberOfManifoldStations === 0 ? '' : numberOfManifoldStations.toString()}
          onChange={handleStationsChange}
          renderValue={() => {
            if (numberOfManifoldStations === 0) {
              return <span>{t('SelectNumberStations')}</span>;
            }
            return <span>{t('SelectedNumberOfStations', { numberOfManifoldStations })}</span>;
          }}
          MenuProps={{ sx: { maxHeight: '224px' } }}
        >
          {[...Array(31)].map((e, i) => {
            const n = i + 2;
            return (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            );
          })}
        </Select>
        <FormHelperText error>{validateNumberStations && !isNumberOfStationsValid() && t('SelectNumberStations')}</FormHelperText>
      </FormControl>
    );
  }

  const addSubsequentPart = async () => {
    createSelectedPart(props.currentGroup?.id, activePart?.partCategoryToAdd);
  };

  function renderPart(
    partCategoryId: number | undefined | null,
    selectedPartIndex: number,
    valveMountingOption?: ValveMountingOption,
    partAssemblyOption?: PartAssemblyOption,
    partCategoryToAdd?: PartCategoryToAdd,
    validationFunction?: () => boolean,
  ) {
    const partCategoryOptions = props.system?.partCategories?.find((category) => category.id === partCategoryId)?.options as Option[];

    const partOption = findValidOption(partCategoryOptions) as Option;
    if (selectedPartIndex === activePart?.partListIndex) {
      activePart.partAssemblyOption = partAssemblyOption;
      activePart.partCategoryToAdd = partCategoryToAdd;
      activePart.valveMountingOption = valveMountingOption;
      activePart.validate = validationFunction;
    }

    const mountingOptionKey =
      valveMountingOption == ValveMountingOption.Individual
        ? process.env.REACT_APP_IS_PRODUCTION === 'true'
          ? valveMountingOption.toString()
          : 'IndividualWithSubbase'
        : valveMountingOption?.toString();

    return (
      <div
        key={valveMountingOption ? valveMountingOption : partCategoryId}
        onClick={() => {
          setActivePart({
            partListIndex: selectedPartIndex,
            partAssemblyOption: partAssemblyOption,
            partCategoryToAdd: partCategoryToAdd,
            valveMountingOption: valveMountingOption,
            validate: validationFunction,
          });
        }}
        className={styles[`${selectedPartIndex === activePart?.partListIndex ? 'part-details-active' : 'part-details'}`]}
      >
        <>
          <div className={styles['part-image']}>
            <img src={`images/silhouette/${partOption?.areaImageUrl ? partOption?.areaImageUrl : 'parkerdefault.svg'}`} />
          </div>
          <div className={styles['part-name']}>{partOption?.displayName}</div>
          <div className={styles['part-description']}>
            {valveMountingOption && <p>{t('ValveMountingOption' + mountingOptionKey)}</p>}
            <p>{partOption?.description}</p>
            {partAssemblyOption && partAssemblyOption.renderNumberOfManifoldStationsControl}
            {valveMountingOption === ValveMountingOption.ExistingManifold && (
              <ManifoldsDropdownList system={props.system as GetSystemResponse} setSelection={setSelectedManifold} shouldValidate={validateManifolds} />
            )}

            {valveMountingOption === ValveMountingOption.NewManifold && renderNumberOfManifoldStationsControl()}
          </div>
        </>
      </div>
    );
  }

  function partAssembliesGroup() {
    return (
      <div
        key="PartAssemblies"
        onClick={() => {
          setActiveGroup(-1);
        }}
        className={styles[`${activeGroup === -1 ? 'group-name-active' : 'group-name'}`]}
      >
        {t('PartAssemblies')}
      </div>
    );
  }

  function renderPartAssembly(partAssemblyCode: string, partAssemblyIndex: number) {
    const codeToPartAssemblyOptionMap: Map<string, PartAssemblyOption> = new Map();

    codeToPartAssemblyOptionMap.set('ManifoldAssembly', {
      categoryId: props.system?.partCategories?.find((partCategory) => partCategory.name === 'PneumaticManifoldMasterPart')?.id as number,
      renderNumberOfManifoldStationsControl: renderNumberOfManifoldStationsControl(),
      validate: validateSelectedNumberOfStations,
    });

    return renderPart(
      codeToPartAssemblyOptionMap.get(partAssemblyCode)?.categoryId as number,
      partAssemblyIndex,
      undefined,
      codeToPartAssemblyOptionMap.get(partAssemblyCode),
      undefined,
      codeToPartAssemblyOptionMap.get(partAssemblyCode)?.validate,
    );
  }

  return (
    <>
      <Modal open={props.modalState} onClose={handleModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <div className={styles['modal-wrapper']}>
          <div className={styles['modal-title']}>
            {t(modalTitleKey)}
            <div>
              <FontAwesomeIcon
                icon="xmark"
                onClick={() => {
                  handleModalClose();
                }}
              />
            </div>
          </div>
          <div className={styles['modal-subtitle']}>Click on a group to view its parts and then click on a part to select it</div>
          <div className={styles['modal-table-title']}>
            <p>{t('Group')}</p>
            <p></p>
            <p>{t('Part')}</p>
            <p>{t('Name')}</p>
            <p>{t('Description')}</p>
          </div>
          <div className={styles['add-parts-table']}>
            <div className={styles['groups-pane']}>
              {groupedPartCategoriesAndPartAssemblies &&
                groupedPartCategoriesAndPartAssemblies?.groupedPartCategories?.map((groupedPartCategory: GroupedPartCategory, groupIndex: number) => {
                  return (
                    <div
                      key={groupIndex}
                      onClick={() => {
                        setActiveGroup(groupIndex);
                      }}
                      className={styles[`${groupIndex === activeGroup ? 'group-name-active' : 'group-name'}`]}
                    >
                      {
                        props.system?.groupDefinitions?.find((groupDefinition) => groupDefinition.id === groupedPartCategory?.group?.groupDefinitionId)
                          ?.displayName
                      }
                    </div>
                  );
                })}

              {props.modalState &&
                props.mode !== AddPartMode.FirstPartForGroup &&
                groupedPartCategoriesAndPartAssemblies?.partAssemblies &&
                groupedPartCategoriesAndPartAssemblies.partAssemblies.length > 0 &&
                partAssembliesGroup()}
            </div>
            <div className={styles['parts-gap']}></div>

            <div className={styles['parts-pane']}>
              {props.modalState &&
                activeGroup > -1 &&
                getPartCategoriesWithoutValve().map((part: PartCategoryToAdd, index: number) =>
                  renderPart(part?.partCategoryId as number, index, undefined, undefined, part),
                )}

              {activeGroup > -1 &&
                valveOptions.map((valveWithMountingOption, index) =>
                  renderPart(
                    valveWithMountingOption.partCategoryToAdd.partCategoryId,
                    getPartCategoriesWithoutValve().length + index,
                    valveWithMountingOption.mountingOption,
                    undefined,
                    valveWithMountingOption.partCategoryToAdd,
                    valveWithMountingOption.validate,
                  ),
                )}
              {activeGroup === -1 &&
                groupedPartCategoriesAndPartAssemblies?.partAssemblies?.map((partAssembly, index) => renderPartAssembly(partAssembly.code as string, index))}
            </div>
          </div>
          <div className={styles['modal-buttons']}>
            <div className={styles['modal-buttons-div']}>
              <Button
                onClick={() => {
                  if (activeGroup == -2 || !activePart) {
                    toast.error(t('NeedToSelectPartInOrderToAdd') as string);
                    return;
                  }
                  const shouldValidateSelectedItem = !!activePart?.validate;
                  const selectedItemIsValid = activePart?.validate && activePart.validate();
                  if (shouldValidateSelectedItem && !selectedItemIsValid) {
                    return;
                  }
                  switch (props.mode) {
                    case AddPartMode.InitialGroupAndPart:
                      if (activeGroup === -1) createPartAssembly();
                      else createGroupAndPart();
                      break;
                    case AddPartMode.SubsequentPartForGroup:
                      if (activePart?.valveMountingOption === ValveMountingOption.NewManifold) {
                        createManifold(true);
                      } else if (activePart?.valveMountingOption === ValveMountingOption.ExistingManifold) {
                        addToExistingManifold();
                      } else {
                        addSubsequentPart();
                      }
                      break;
                    case AddPartMode.FirstPartForGroup:
                      createFirstPart();
                      break;
                    default:
                      break;
                  }
                  handleModalClose();
                }}
                variant="outlined"
                className={styles['add-part-button']}
              >
                {t('Add')}
              </Button>
              <Button
                onClick={() => {
                  handleModalClose();
                }}
                variant="outlined"
                className={styles['cancel-button']}
              >
                {t('Cancel')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {showOptimizedManifoldAlert &&
        confirmManifoldRevert(
          showOptimizedManifoldAlert,
          setShowOptimizedManifoldAlert,
          setConfirmedRevertManifold,
          props.system?.groups?.find((manifoldGroup) => manifoldGroup.id === selectedManifoldGroupId)?.id as number,
        )}
    </>
  );
};
